// Match alerts by e-mail — the non-visual part of the bell menu.
//
// A signed-in person picks a level (off / ≥ 80 % / ≥ 90 %). The tender
// Lambda keeps that as a topic preference on the person's contact in the SES
// contact list (GET/PUT /alerts). It takes effect at once — the login e-mail
// needs no confirmation. The reconciler mails every opted-in person once a
// day with the tenders that reached the level for the first time; every mail
// carries an unsubscribe link, which does the same as "Off" here.
import config from './config.json';

export type AlertStatus = 'off' | 'active';

export interface AlertState {
    email: string;
    available: boolean;   // false when the company has no alert topics yet
    levels: number[];     // the levels a person can pick, e.g. [80, 90]
    level: number;        // 0 = off
    status: AlertStatus;
}

// Shown until the API answers; the API's `levels` (from the topics terraform created) win.
export const DEFAULT_LEVELS = [80, 90];

const tenderApiUrl = (): string => (config as { api: { tender: { url: string } } }).api.tender.url;

const authHeaders = (auth: any): Record<string, string> => {
    const apiKey = auth?.user?.profile?.['custom:api_key'] as string | undefined;
    const idToken = auth?.user?.id_token as string | undefined;
    if (!auth?.isAuthenticated || !idToken || !apiKey) throw new Error('Authentication is required.');
    return { 'Content-Type': 'application/json', 'X-Api-Key': apiKey, Authorization: idToken };
};

export const fetchAlertState = async (auth: any): Promise<AlertState> => {
    const res = await fetch(`${tenderApiUrl()}/alerts`, { headers: authHeaders(auth) });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not read your alert setting');
    return res.json();
};

export const saveAlertLevel = async (auth: any, level: number): Promise<AlertState> => {
    const res = await fetch(`${tenderApiUrl()}/alerts`, {
        method: 'PUT', headers: authHeaders(auth), body: JSON.stringify({ level }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save your alert setting');
    return res.json();
};

export const levelLabel = (level: number): string =>
    level > 0 ? `New tenders scoring ≥ ${level} %` : 'Off';

// One sentence for the menu, from the state the API returned.
export const describeAlertState = (state: AlertState): string => {
    if (!state.available) return 'Alerts are not set up for your company yet.';
    if (state.status === 'active') {
        return `Active: one mail a day when a tender reaches ${state.level} % for the first time.`;
    }
    return 'Off. Pick a level to get one mail a day with the new tenders above it.';
};
